/** NOTIF1 T13: pure view rules for actions + feed (badge, chips, sentences, day groups). */
import {
  ACTION_TARGET, ACTION_TYPE, BADGE_MAX, EVENT_TYPE, EXTERNAL_TARGET_HOSTS, PRIORITY, REGISTRATION_EVENTS,
  SUGGESTION_FALLBACK_TARGET, SUGGESTION_TARGET,
} from '../../constants/inbox';
import { retryLink } from '../urssaf/urssafRetryLink';
import { INBOX_COPY as COPY } from '../../constants/inbox_messages.fr';
import { formatLongDate } from '../../utils/formatters';

const DAY_MS = 86400000;
const TIME_LENGTH = 5; // 'HH:MM'
const DAY_MONTH = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
const WEEKDAY_DAY_MONTH = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const CLOCK = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

export const BADGE_TONE = Object.freeze({ DANGER: 'danger', BRAND: 'brand', DOT: 'dot' });
export const CHIP_TONE = Object.freeze({ DANGER: 'danger', WARNING: 'warning', NEUTRAL: 'neutral', SUCCESS: 'success' });

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const dayDiff = (date, now) => Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);

/** Badge = P1 + P2 count capped "9+"; red if any P1, brand otherwise; dot if no action but unread events. */
export function badgeState(summary) {
  const { p1 = 0, p2 = 0, unread = 0 } = summary || {};
  const count = p1 + p2;
  if (count) {
    return { count, label: count > BADGE_MAX ? `${BADGE_MAX}+` : String(count), tone: p1 ? BADGE_TONE.DANGER : BADGE_TONE.BRAND };
  }
  if (unread) return { count: 0, label: '', tone: BADGE_TONE.DOT };
  return null;
}

/** "mercredi 7 octobre, 18h30" */
export function formatLessonWhen(lessonDate, startTime) {
  if (!lessonDate) return '';
  const date = WEEKDAY_DAY_MONTH.format(new Date(`${String(lessonDate).slice(0, 10)}T12:00:00`));
  if (!startTime) return date;
  const [hours, minutes] = String(startTime).slice(0, TIME_LENGTH).split(':');
  return `${date}, ${Number(hours)}h${minutes}`;
}

export function actionTitle(action) {
  const spec = COPY.actions[action.type];
  if (!spec) return '';
  return typeof spec.title === 'function' ? spec.title(action.payload || {}) : spec.title;
}

export function actionMeta(action) {
  const spec = COPY.actions[action.type];
  const payload = action.payload || {};
  return spec ? spec.meta({ ...payload, when: formatLessonWhen(payload.lesson_date, payload.start_time) }) : '';
}

/** Chip next to the title: when it is due, or why it needs a decision. */
export function actionChip(action, now = new Date()) {
  const payload = action.payload || {};
  if (action.type === ACTION_TYPE.SCHEDULE_CONFLICT && payload.day) {
    return { label: COPY.chips.weekly(payload.day), tone: CHIP_TONE.DANGER };
  }
  if (action.type === ACTION_TYPE.URSSAF_DECLARATION && action.due_at) {
    return { label: COPY.chips.before(DAY_MONTH.format(new Date(action.due_at))), tone: action.priority === PRIORITY.URGENT ? CHIP_TONE.DANGER : CHIP_TONE.WARNING };
  }
  if (action.priority === PRIORITY.URGENT && action.due_at) {
    const diff = dayDiff(new Date(action.due_at), now);
    if (diff === 0) return { label: COPY.today, tone: CHIP_TONE.DANGER };
    if (diff === 1) return { label: COPY.chips.tomorrow, tone: CHIP_TONE.DANGER };
    return { label: COPY.chips.urgent, tone: CHIP_TONE.DANGER };
  }
  if (action.type === ACTION_TYPE.LESSON_REQUEST) return { label: COPY.chips.decision, tone: CHIP_TONE.WARNING };
  return null;
}

/** Link label per action type (types without one get no link button). */
export const ACTION_LINK_LABEL = Object.freeze({
  [ACTION_TYPE.SCHEDULE_CONFLICT]: COPY.openAgenda,
  [ACTION_TYPE.URSSAF_DECLARATION]: COPY.declare,
  [ACTION_TYPE.URSSAF_PAYMENT_ISSUE]: COPY.view,
  [ACTION_TYPE.SUGGESTION]: COPY.open,
  [ACTION_TYPE.URSSAF_DEMAND_RETRY]: COPY.retry,
});

/** https URL on an allowed official host → normalised href; anything else → null. */
const officialExternalTarget = (target) => {
  if (typeof target !== 'string') return null;
  try {
    const url = new URL(target);
    return url.protocol === 'https:' && EXTERNAL_TARGET_HOSTS.includes(url.hostname) ? url.href : null;
  } catch {
    return null;
  }
};

export const isExternalTarget = (target) => officialExternalTarget(target) !== null;

export const actionTarget = (action) => {
  const externalTarget = officialExternalTarget(action.payload?.meta?.nav?.url);
  if (externalTarget) return externalTarget;
  if (action.type === ACTION_TYPE.SUGGESTION) return SUGGESTION_TARGET[action.payload?.type] || SUGGESTION_FALLBACK_TARGET;
  if (action.type === ACTION_TYPE.URSSAF_DEMAND_RETRY) {
    return retryLink(action.payload.family_id, action.payload.lesson_ids, action.payload.kind);
  }
  return ACTION_TARGET[action.type];
};

export const priorityTone = (priority) => (
  priority === PRIORITY.URGENT ? CHIP_TONE.DANGER : priority === PRIORITY.DECISION ? CHIP_TONE.WARNING : CHIP_TONE.NEUTRAL
);

export function dayLabel(date, now = new Date()) {
  const diff = dayDiff(new Date(date), now);
  if (diff === 0) return COPY.today;
  if (diff === -1) return COPY.yesterday;
  return formatLongDate(date);
}

export const formatClock = (date) => CLOCK.format(new Date(date));

const dayKey = (date) => startOfDay(new Date(date)).getTime();

/**
 * Same-day consecutive events of one type without pending decisions → one group
 * ("Famille A et 2 autres familles ont…"). Returns [{day, groups: [{key, type, items, unread}]}].
 */
export function groupFeed(items) {
  const days = [];
  items.forEach((item) => {
    const key = dayKey(item.created_at);
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, date: item.created_at, groups: [] };
      days.push(day);
    }
    const last = day.groups[day.groups.length - 1];
    const groupable = !item.actions?.length && !item.state && !REGISTRATION_EVENTS.includes(item.type);
    if (last && groupable && last.groupable && last.type === item.type) {
      last.items.push(item);
      last.unread = last.unread || !item.read_at;
      return;
    }
    day.groups.push({ key: item.notification_id, type: item.type, items: [item], unread: !item.read_at, groupable });
  });
  return days;
}

const ACTOR = Object.freeze({ TEACHER: 'teacher', FAMILY: 'family', URSSAF: 'urssaf' });

/**
 * N30 row → "Séance du mardi 7 octobre enregistrée" / "Paiement de 60,00 € enregistré".
 * `reader` = 'teacher' | 'family': the actor is named only when it is not the reader.
 */
const DATED_EVENTS = Object.freeze({
  [EVENT_TYPE.LESSON_REGISTERED]: 'lesson_date', [EVENT_TYPE.LESSON_DELETED]: 'lesson_date',
  [EVENT_TYPE.LESSON_CANCELLED]: 'lesson_date', [EVENT_TYPE.PAYMENT_UPDATED]: 'payment_date',
  [EVENT_TYPE.SESSION_SKIPPED]: 'lesson_date', [EVENT_TYPE.SESSION_MOVED]: 'lesson_date',
});
// Reminders that read the same for both sides: no « par … » suffix.
const UNSIGNED_EVENTS = Object.freeze([EVENT_TYPE.URSSAF_VALIDATION_PENDING, EVENT_TYPE.URSSAF_DEMAND_RETRY]);
const LESSON_EVENTS = Object.freeze([
  EVENT_TYPE.LESSON_REGISTERED, EVENT_TYPE.LESSON_DELETED, EVENT_TYPE.LESSON_CANCELLED,
  EVENT_TYPE.SESSION_SKIPPED, EVENT_TYPE.SESSION_MOVED,
]);
const longDay = (value) => (value ? WEEKDAY_DAY_MONTH.format(new Date(`${String(value).slice(0, 10)}T12:00:00`)) : '');

export function registrationSentence(item, reader) {
  const p = item.payload || {};
  const sentence = COPY.registered[item.type] || COPY.registered.payment_registered;
  const student = [item.student_first_name, item.student_last_name].filter(Boolean).join(' ');
  const family = item.subject_family_last_name;
  const actorNamesFamily = item.actor_type === ACTOR.FAMILY && item.actor_type !== reader
    && item.family_last_name === family;
  const context = LESSON_EVENTS.includes(item.type)
    ? (student ? COPY.registered.forStudent(student) : '')
    : (family && !actorNamesFamily ? COPY.registered.forFamily(family) : '');
  const base = (DATED_EVENTS[item.type] ? sentence(longDay(p[DATED_EVENTS[item.type]])) : sentence(COPY.amount(p.amount || 0))) + context;
  if (item.actor_type === reader || UNSIGNED_EVENTS.includes(item.type)) return base;
  if (item.actor_type === ACTOR.URSSAF) return `${base}${COPY.registered.byUrssaf}`;
  if (item.actor_type === ACTOR.FAMILY) return `${base}${COPY.registered.byFamily(item.family_last_name)}`;
  return `${base}${COPY.registered.byTeacher}`;
}

/** Browser pop-up text for one feed row (N31). */
export function notificationMessage(item, reader) {
  if (REGISTRATION_EVENTS.includes(item.type)) return { title: COPY.browser.title, body: registrationSentence(item, reader) };
  const { actor, rest } = eventSentence({ type: item.type, items: [item] });
  return { title: COPY.browser.title, body: `${actor} ${rest}` };
}

/** "Famille Fictif et 2 autres familles a envoyé…" parts: {actor, rest}. */
export function eventSentence(group) {
  if (REGISTRATION_EVENTS.includes(group.type)) {
    return { actor: '', rest: registrationSentence(group.items[0], ACTOR.TEACHER) };
  }
  const names = [...new Set(group.items.map((item) => item.family_last_name).filter(Boolean))];
  const first = names[0] ? `Famille ${names[0]}` : COPY.familyFallback;
  const others = names.length > 1 ? COPY.others(names.length - 1) : '';
  return { actor: `${first}${others}`, rest: COPY.events[group.type] || COPY.eventFallback };
}
